import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';

import { COMMENT_MAX_LEN } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/constants/PlagiarismFlagConstants';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/PlagiarismFlagCommentInput';

type Props = {
  onUpdateComment: (comment: string) => void;
  pendingComment: string;
  vivaExamEnabled: boolean;
};

/**
 * Simple input for adding new comments to a plagiarism case
 */

export default class PlagiarismFlagCommentInput extends React.Component<Props> {
  declare textAreaRef: HTMLTextAreaElement | null | undefined;

  componentDidMount() {
    this.resizeTextArea();
  }

  componentDidUpdate(prevProps: Props) {
    const { pendingComment } = this.props;
    if (prevProps.pendingComment !== pendingComment) {
      this.resizeTextArea();
    }
  }

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { onUpdateComment } = this.props;
    // @ts-expect-error TSMIGRATION
    onUpdateComment(e.target.value || '');
  };

  resizeTextArea = () => {
    if (this.textAreaRef) {
      const { scrollHeight, offsetHeight } = this.textAreaRef;

      this.textAreaRef.style.height = 'auto';
      const autoHeight = this.textAreaRef.scrollHeight;
      if (scrollHeight > offsetHeight) {
        // if the total container is larger then just what is visible, set the height to be the total container
        this.textAreaRef.style.height = scrollHeight + 'px';
        this.textAreaRef.scrollTop = scrollHeight;
      } else if (autoHeight < offsetHeight) {
        // else if the size of just the content has shrunk, set the height to be just the size of the content
        this.textAreaRef.style.height = autoHeight + 'px';
        this.textAreaRef.scrollTop = autoHeight;
      } else {
        // else set the height back to before the auto adjustment to avoid repainting/jittering
        this.textAreaRef.style.height = offsetHeight + 'px';
        this.textAreaRef.scrollTop = offsetHeight;
      }
    }
  };

  render() {
    const { pendingComment, vivaExamEnabled } = this.props;
    const remainingCharacters = COMMENT_MAX_LEN - pendingComment.length;

    const rootClassName = vivaExamEnabled
      ? 'rc-PlagiarismFlagCommentInput no-border-top'
      : 'rc-PlagiarismFlagCommentInput';

    return (
      <Box rootClassName={rootClassName} flexDirection="column">
        <Box rootClassName="input-header" alignItems="center">
          <Typography2 variant="subtitleMedium" component="label">
            {_t('Comment')}
          </Typography2>
        </Box>
        <Typography2 component="p" variant="bodySecondary">
          {_t('Comments are for internal staff only. Comments may be added without a status update.')}
        </Typography2>
        <textarea
          className="comment-input pii-hide"
          // @ts-expect-error TSMIGRATION
          onChange={this.handleChange}
          value={pendingComment}
          placeholder={_t('Add Comment...')}
          rows={1}
          ref={(ref) => {
            this.textAreaRef = ref;
          }}
          maxLength={COMMENT_MAX_LEN}
        />
        <Typography2 component="p" variant="bodySecondary" className="sub-label">
          <FormattedMessage
            message={_t('{charactersRemaining, plural, =1 {# character} other {# characters}} remaining')}
            charactersRemaining={remainingCharacters}
          />
        </Typography2>
      </Box>
    );
  }
}
